/**
 * useCheckoutValidation Composable
 * Handles checkout form validation logic
 * Single Responsibility: Form validation only
 * Compatible with Vue 2
 */

/**
 * Validates customer details
 * @param {Object} details - Customer details object
 * @returns {boolean} True if valid
 */
export function validateCustomerDetails(details) {
  if (!details) return false;

  // Basic validation - details.phone is combined in some contexts,
  // but in useCheckout it might be just digits or combined.
  // The Checkout.vue handles internal validation now, but for safety:
  return (
    details.firstName?.length > 1 &&
    details.lastName?.length > 1 &&
    details.email?.includes("@") &&
    details.phone?.replace(/\D/g, "").length >= 7
  );
}

/**
 * Validates card details for Stripe payment
 * @param {Object} cardDetails - Card details object
 * @returns {boolean} True if valid
 */
export function validateStripeCardDetails(cardDetails) {
  if (!cardDetails) return false;

  // Stripe only requires name on card (Stripe.js handles the rest)
  return cardDetails.name?.trim().length > 2;
}

/**
 * Validates card details for manual card processing (USAePay, etc.)
 * @param {Object} cardDetails - Card details object
 * @returns {boolean} True if valid
 */
export function validateManualCardDetails(cardDetails) {
  if (!cardDetails) return false;

  return (
    cardDetails.name?.length > 2 &&
    cardDetails.number?.replace(/\s+/g, "").length > 12 &&
    cardDetails.expiration?.length >= 4 &&
    cardDetails.cvv?.length >= 3
  );
}

/**
 * Validates checkout form based on payment method
 * @param {Object} params
 * @param {Object} params.customerDetails - Customer details
 * @param {Object} params.paymentMethod - Payment method object
 * @param {Object} params.cardDetails - Card details (if required)
 * @returns {boolean} True if form is valid
 */
export function validateCheckoutForm({
  customerDetails,
  paymentMethod,
  cardDetails
}) {
  // Must have customer details
  if (!validateCustomerDetails(customerDetails)) {
    return false;
  }

  // Must have a payment method selected
  if (!paymentMethod) {
    return false;
  }

  // If payment method doesn't require card processing, form is valid
  if (!paymentMethod.card_processing) {
    return true;
  }

  // Validate card details based on payment processor
  if (paymentMethod.payment_processor === "Stripe") {
    return validateStripeCardDetails(cardDetails);
  } else if (paymentMethod.payment_processor === "Dejavoo Online") {
    // Card data lives in the Freedom to Design secure fields, which validate
    // themselves when the payment token is requested on submit.
    return true;
  } else {
    return validateManualCardDetails(cardDetails);
  }
}
